import { useEffect, useState } from 'react';
import type { TelegramWebApp } from '@/types';

/**
 * The Telegram WebApp object when running inside a real Telegram Mini App context, else null.
 * Synchronous and side-effect free — safe to call during render.
 */
export function getTelegramWebApp(): TelegramWebApp | null {
  const tg = window.Telegram?.WebApp;
  const isRealTelegramContext = !!(
    tg &&
    tg.initData &&
    (tg.initData.length > 0 || window.location.search.includes('tgWebAppData'))
  );
  return isRealTelegramContext && tg ? tg : null;
}

export const useTelegram = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);

  useEffect(() => {
    const tg = getTelegramWebApp();

    if (tg) {
      tg.ready();
      tg.expand();
      // Disable pull-to-close gesture (prevents app from closing when scrolling up at top)
      if (typeof tg.disableVerticalSwipes === 'function') {
        tg.disableVerticalSwipes();
      }
      setWebApp(tg);
      setIsLoaded(true);
    } else {
      // Fallback for development/browser mode  
      setWebApp(null);
      setIsLoaded(true);
    }
  }, []);

  const user = webApp?.initDataUnsafe?.user;
  const startParam = webApp?.initDataUnsafe?.start_param ?? null;
  const isInTelegram = !!webApp;
  
  const openLink = (url: string) => {
    if (webApp) {
      webApp.openLink(url);
    } else {
      window.open(url, '_blank');
    }
  };

  const openTelegramLink = (url: string) => {
    if (webApp) {
      webApp.openTelegramLink(url);
    } else {
      window.open(url, '_blank');
    }
  };

  const showMainButton = (text: string, onClick: () => void) => {
    if (webApp?.MainButton) {
      webApp.MainButton.setText(text);
      webApp.MainButton.onClick(onClick);
      webApp.MainButton.show();
    }
  };

  const hideMainButton = () => {
    if (webApp?.MainButton) {
      webApp.MainButton.hide();
    }
  };

  const showBackButton = (onClick: () => void) => {
    if (webApp?.BackButton) {
      webApp.BackButton.onClick(onClick);
      webApp.BackButton.show();
    }
  };

  const hideBackButton = () => {
    if (webApp?.BackButton) {
      webApp.BackButton.hide();
    }
  };

  const close = () => {
    if (webApp) {
      webApp.close();
    }
  };

  const sendData = (data: string) => {
    if (webApp) {
      webApp.sendData(data);
    }
  };

  return {
    webApp,
    user,
    startParam,
    isLoaded,
    isInTelegram,
    colorScheme: webApp?.colorScheme || 'light',
    themeParams: webApp?.themeParams || {},
    initData: webApp?.initData || '',
    openLink,
    openTelegramLink,
    showMainButton,
    hideMainButton,
    showBackButton,
    hideBackButton,
    close,
    sendData,
  };
};

export default useTelegram;